
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { LoginRequestModel } from '@/models/login_request';

@Component
export default class LoginRequestPopup extends Vue {
  @Prop({ required: true }) public request!: LoginRequestModel;
  @Prop({ required: true }) public value!: boolean;

  public approving: boolean = false;
  public rejecting: boolean = false;
  public timeRemaining: string = '';
  public timeRemainingInterval: any = null;
  public showConfirmDialog: boolean = false;
  public confirmAction: 'approve' | 'reject' | null = null;

  get show() {
    return this.value;
  }

  set show(value: boolean) {
    this.$emit('input', value);
  }

  get loading(): boolean {
    return this.approving || this.rejecting;
  }

  get timeRemainingClass(): string {
    const diff = this.getTimeDiff();
    if (diff <= 0) {
      return 'error--text font-weight-bold';
    } else if (diff < 3600000) { // Less than 1 hour
      return 'warning--text font-weight-bold';
    }
    return 'success--text font-weight-bold';
  }

  public mounted() {
    if (this.show) {
      this.updateTimeRemaining();
      this.startTimeRemainingUpdater();
    }
  }

  public beforeDestroy() {
    this.stopTimeRemainingUpdater();
  }

  @Watch('show')
  public onShowChanged(newVal: boolean) {
    if (newVal) {
      this.updateTimeRemaining();
      this.startTimeRemainingUpdater();
    } else {
      this.stopTimeRemainingUpdater();
    }
  }

  public startTimeRemainingUpdater() {
    this.stopTimeRemainingUpdater(); // Clear any existing interval
    this.timeRemainingInterval = setInterval(() => {
      this.updateTimeRemaining();
    }, 1000); // Update every second
  }

  public stopTimeRemainingUpdater() {
    if (this.timeRemainingInterval) {
      clearInterval(this.timeRemainingInterval);
      this.timeRemainingInterval = null;
    }
  }

  public getTimeDiff(): number {
    if (!this.request.expiresAt) {
      return 0;
    }
    const now = new Date();
    const expiresAt = new Date(this.request.expiresAt);
    return expiresAt.getTime() - now.getTime();
  }

  public updateTimeRemaining() {
    const diff = this.getTimeDiff();

    if (diff <= 0) {
      this.timeRemaining = 'Expired';
      this.stopTimeRemainingUpdater();
      return;
    }

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (hours > 0) {
      this.timeRemaining = `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      this.timeRemaining = `${minutes}m ${seconds}s`;
    } else {
      this.timeRemaining = `${seconds}s`;
    }
  }

  public confirmApprove() {
    this.confirmAction = 'approve';
    this.showConfirmDialog = true;
  }

  public async handleApprove() {
    this.approving = true;

    try {
      await this.request.respond('approved');

      this.$emit('request-responded', this.request);
      this.show = false;

      // Show success snackbar
      this.$emit('show-snackbar', {
        message: 'Access approved successfully!',
        color: 'success',
      });
    } catch (error: any) {
      console.error('Error approving request:', error);

      let errorMsg = 'Failed to approve request. Please try again.';
      if (error.response?.status === 410) {
        errorMsg = 'This request has expired.';
      } else if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      }

      // Show error snackbar
      this.$emit('show-snackbar', {
        message: errorMsg,
        color: 'error',
      });
    } finally {
      this.approving = false;
    }
  }

  public confirmReject() {
    this.confirmAction = 'reject';
    this.showConfirmDialog = true;
  }

  public async handleReject() {
    this.rejecting = true;

    try {
      await this.request.respond('rejected');

      this.$emit('request-responded', this.request);
      this.show = false;

      // Show success snackbar
      this.$emit('show-snackbar', {
        message: 'Request rejected successfully.',
        color: 'success',
      });
    } catch (error: any) {
      console.error('Error rejecting request:', error);

      let errorMsg = 'Failed to reject request. Please try again.';
      if (error.response?.status === 410) {
        errorMsg = 'This request has expired.';
      } else if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      }

      // Show error snackbar
      this.$emit('show-snackbar', {
        message: errorMsg,
        color: 'error',
      });
    } finally {
      this.rejecting = false;
    }
  }

  public async confirmDialogAction() {
    this.showConfirmDialog = false;
    if (this.confirmAction === 'approve') {
      await this.handleApprove();
    } else if (this.confirmAction === 'reject') {
      await this.handleReject();
    }
  }

  public formatDate(dateString?: string): string {
    if (!dateString) { return 'N/A'; }
    const date = new Date(dateString);
    return date.toLocaleString();
  }
}
